import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  MinusIcon,
} from '@heroicons/react/24/outline'
import {
  migrateWarehouseVendorsColumnWidths,
  WAREHOUSE_VENDORS_COLUMN_KEYS,
  WAREHOUSE_VENDORS_DEFAULT_WIDTHS,
  WAREHOUSE_VENDORS_PAGE_KEY,
} from '@/components/warehouseVendorsTableConfig'
import { useSortableTable } from '@/hooks/useSortableTable'
import type { SortDirection } from '@/hooks/useSortableTable'
import {
  createWarehouseVendor,
  deleteWarehouseVendor,
  listWarehouseVendors,
  updateWarehouseVendor,
} from '@/services/warehouse'
import type { WarehouseVendorItem } from '@/services/warehouseTypes'
import { toast } from '@/store/toastStore'
import { ColumnResizeHandle, HoverHelp, useColumnWidths } from '@/tableControls'
import { formatApiError } from '@/utils/apiError'
import { fixedColumnStyle } from '@/utils/warehouseTableHelpers'

type LocalVendorRow = WarehouseVendorItem & { _clientId: string }

let nextClientId = 0

function newClientId(): string {
  nextClientId += 1
  return `vendor-${nextClientId}`
}

function toLocalRow(vendor: WarehouseVendorItem): LocalVendorRow {
  return { ...vendor, _clientId: newClientId() }
}

function vendorSortValue(row: LocalVendorRow, key: string): string | number {
  if (key === 'id') return row.id
  if (key === 'name') return row.name
  if (key === 'qbo_vendor_id') return row.qbo_vendor_id ?? ''
  return row.name
}

function VendorSortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  width,
  onStartResize,
  style,
}: {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  columnKey: string
  width: number
  onStartResize: (key: string, event: ReactMouseEvent) => void
  style?: CSSProperties
}) {
  const isSorted = sortDirection !== null

  return (
    <th
      className={`sortable relative ${isSorted ? 'sorted' : ''} ${className}`}
      style={{
        ...style,
        ...fixedColumnStyle(width),
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 pr-2">
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronUpDownIcon className="h-3 w-3" />
          )}
        </span>
      </span>
      <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
    </th>
  )
}

type WarehouseVendorsCardProps = {
  busy?: boolean
}

export default function WarehouseVendorsCard({ busy = false }: WarehouseVendorsCardProps) {
  const [rows, setRows] = useState<LocalVendorRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addVendorOpen, setAddVendorOpen] = useState(false)
  const [newVendorName, setNewVendorName] = useState('')
  const [newQboVendorId, setNewQboVendorId] = useState('')
  const [addVendorError, setAddVendorError] = useState<string | null>(null)
  const [addVendorSubmitting, setAddVendorSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())

  const {
    sortedData: sortedRows,
    requestSort,
    getSortDirection,
  } = useSortableTable(rows, { key: 'name', direction: 'asc' }, vendorSortValue)

  useEffect(() => {
    migrateWarehouseVendorsColumnWidths()
  }, [])

  const { getWidth, startResize, totalWidth } = useColumnWidths({
    pageKey: WAREHOUSE_VENDORS_PAGE_KEY,
    columnKeys: [...WAREHOUSE_VENDORS_COLUMN_KEYS],
    defaults: WAREHOUSE_VENDORS_DEFAULT_WIDTHS,
  })

  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: totalWidth,
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => fixedColumnStyle(getWidth(columnKey)),
    [getWidth]
  )

  const loadRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const vendors = await listWarehouseVendors()
      setRows(vendors.map(toLocalRow))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendors')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadRows()
  }, [loadRows])

  useEffect(() => {
    return () => {
      for (const timer of Object.values(debounceRef.current)) {
        clearTimeout(timer)
      }
    }
  }, [])

  const persistVendor = useCallback(
    (vendorId: number, payload: { name?: string; qbo_vendor_id?: string }) => {
      const saveTask = async () => {
        setError(null)
        try {
          const updated = await updateWarehouseVendor(vendorId, payload)
          setRows((prev) =>
            prev.map((row) =>
              row.id === vendorId ? { ...updated, _clientId: row._clientId } : row
            )
          )
        } catch (err) {
          const message = formatApiError(err, 'Failed to save vendor')
          setError(message)
          toast.error(message)
          throw err
        }
      }
      const promise = saveChainRef.current.then(saveTask)
      saveChainRef.current = promise.catch(() => undefined)
      return promise
    },
    []
  )

  const scheduleVendorSave = useCallback(
    (
      vendorId: number,
      field: 'name' | 'qbo_vendor_id',
      value: string,
      previousValue: string
    ) => {
      const trimmed = value.trim()
      if (field === 'qbo_vendor_id' && !trimmed) return
      if (!trimmed || trimmed === previousValue.trim()) return
      const debounceKey = `${vendorId}:${field}`
      if (debounceRef.current[debounceKey]) {
        clearTimeout(debounceRef.current[debounceKey])
      }
      debounceRef.current[debounceKey] = setTimeout(() => {
        delete debounceRef.current[debounceKey]
        void persistVendor(vendorId, { [field]: trimmed })
      }, 400)
    },
    [persistVendor]
  )

  function updateName(clientId: string, name: string) {
    setRows((prev) => {
      const current = prev.find((row) => row._clientId === clientId)
      if (!current) return prev
      const next = prev.map((row) => (row._clientId === clientId ? { ...row, name } : row))
      scheduleVendorSave(current.id, 'name', name, current.name)
      return next
    })
  }

  function updateQboVendorId(clientId: string, qboVendorId: string) {
    setRows((prev) => {
      const current = prev.find((row) => row._clientId === clientId)
      if (!current) return prev
      const next = prev.map((row) =>
        row._clientId === clientId ? { ...row, qbo_vendor_id: qboVendorId } : row
      )
      scheduleVendorSave(
        current.id,
        'qbo_vendor_id',
        qboVendorId,
        current.qbo_vendor_id ?? ''
      )
      return next
    })
  }

  function openAddVendorModal() {
    setNewVendorName('')
    setNewQboVendorId('')
    setAddVendorError(null)
    setAddVendorOpen(true)
  }

  function closeAddVendorModal() {
    if (addVendorSubmitting) return
    setAddVendorOpen(false)
    setNewVendorName('')
    setNewQboVendorId('')
    setAddVendorError(null)
  }

  async function submitAddVendor() {
    const name = newVendorName.trim()
    const qboVendorId = newQboVendorId.trim()
    if (!name) {
      setAddVendorError('Vendor name is required.')
      return
    }
    if (!qboVendorId) {
      setAddVendorError('QBO vendor ID is required.')
      return
    }
    setAddVendorSubmitting(true)
    setAddVendorError(null)
    try {
      const created = await createWarehouseVendor({ name, qbo_vendor_id: qboVendorId })
      setRows((prev) => [...prev, toLocalRow(created)])
      toast.success(`Added vendor ${created.name}`)
      setAddVendorOpen(false)
      setNewVendorName('')
      setNewQboVendorId('')
    } catch (err) {
      setAddVendorError(formatApiError(err, 'Failed to add vendor'))
    } finally {
      setAddVendorSubmitting(false)
    }
  }

  async function handleDelete(vendor: LocalVendorRow) {
    setDeletingId(vendor.id)
    try {
      await deleteWarehouseVendor(vendor.id)
      setRows((prev) => prev.filter((row) => row.id !== vendor.id))
      toast.success(`Deleted vendor ${vendor.name}`)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete vendor'))
    } finally {
      setDeletingId(null)
    }
  }

  const disabled = busy || loading || addVendorSubmitting || deletingId != null

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="container-title">Vendors</h2>
          <p className="mt-1 text-xs text-gray-500">
            Product invoice vendors used when uploading PDFs and assigning invoices. Each vendor needs
            a QuickBooks vendor ID for QBO bill sync. Vendors without a custom parser can be added
            for labeling, but PDF upload requires a supported parser.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary shrink-0 text-sm"
          disabled={disabled}
          onClick={openAddVendorModal}
        >
          Add vendor
        </button>
      </div>

      {loading ? (
        <div className="mt-4 flex shrink-0 items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Loading vendors…
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">No vendors yet. Click Add vendor to create one.</p>
      ) : (
        <div className="table-scroll-mobile mt-4 min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-lg border border-gray-200">
          <table
            className="table-forecast border-separate border-spacing-0"
            style={tableStyle}
          >
            <thead>
              <tr>
                <VendorSortableHeader
                  label={
                    <HoverHelp label="Vendor ID" aria-label="Vendor ID help">
                      Internal Bookkeeper id for this vendor record.
                    </HoverHelp>
                  }
                  sortKey="id"
                  sortDirection={getSortDirection('id')}
                  onSort={requestSort}
                  columnKey="id"
                  width={getWidth('id')}
                  onStartResize={startResize}
                />
                <VendorSortableHeader
                  label={
                    <HoverHelp label="Vendor name" aria-label="Vendor name help">
                      Display name shown on product invoices and upload flows.
                    </HoverHelp>
                  }
                  sortKey="name"
                  sortDirection={getSortDirection('name')}
                  onSort={requestSort}
                  columnKey="name"
                  width={getWidth('name')}
                  onStartResize={startResize}
                />
                <VendorSortableHeader
                  label={
                    <HoverHelp label="QBO vendor ID" aria-label="QBO vendor ID help">
                      QuickBooks Online vendor record id used when syncing vendor bills for this
                      warehouse vendor. Required when creating a vendor; leave empty only for legacy
                      rows until configured.
                    </HoverHelp>
                  }
                  sortKey="qbo_vendor_id"
                  sortDirection={getSortDirection('qbo_vendor_id')}
                  onSort={requestSort}
                  columnKey="qbo_vendor_id"
                  width={getWidth('qbo_vendor_id')}
                  onStartResize={startResize}
                />
                <th
                  className="relative"
                  style={{
                    ...fixedColumnStyle(getWidth('actions')),
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <span className="sr-only">Actions</span>
                  <ColumnResizeHandle columnKey="actions" onStartResize={startResize} />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row._clientId}>
                  <td className="tabular-nums text-gray-700" style={cellStyle('id')}>
                    <div className="flex items-center gap-1.5">
                      <span>{row.id}</span>
                      {!row.has_invoice_parser ? (
                        <HoverHelp
                          aria-label="No invoice parser"
                          label={
                            <span className="inline-flex text-amber-500">
                              <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          }
                        >
                          <p>
                            No custom invoice parser is configured for this vendor yet. PDF
                            upload is limited to vendors with parsers (for example Halo or
                            Webstaurant).
                          </p>
                        </HoverHelp>
                      ) : null}
                    </div>
                  </td>
                  <td style={cellStyle('name')}>
                    <input
                      type="text"
                      className="input h-8 w-full min-w-0 text-sm"
                      value={row.name}
                      disabled={disabled || deletingId === row.id}
                      onChange={(e) => updateName(row._clientId, e.target.value)}
                    />
                  </td>
                  <td style={cellStyle('qbo_vendor_id')}>
                    <input
                      type="text"
                      className="input h-8 w-full min-w-0 text-sm"
                      value={row.qbo_vendor_id ?? ''}
                      placeholder="QBO vendor id"
                      disabled={disabled || deletingId === row.id}
                      onChange={(e) => updateQboVendorId(row._clientId, e.target.value)}
                    />
                  </td>
                  <td style={cellStyle('actions')}>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      aria-label={`Delete vendor ${row.name}`}
                      disabled={disabled || deletingId === row.id}
                      onClick={() => void handleDelete(row)}
                    >
                      <MinusIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error ? (
        <div className="mt-3 shrink-0">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      ) : null}

      {addVendorOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={closeAddVendorModal}
        >
          <div
            className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-gray-900">Add vendor</h3>
            <p className="mt-1 text-xs text-gray-500">
              Create a vendor record for labeling product invoices and QBO bill sync. A custom PDF
              parser must be built separately before uploads work for this vendor.
            </p>
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p>
                  New vendors do not include an invoice parser yet. You can assign them on
                  invoices, but PDF upload will stay limited to parser-supported vendors until
                  parser work is completed.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="new-vendor-name" className="text-xs font-medium text-gray-700">
                Vendor name
              </label>
              <input
                id="new-vendor-name"
                type="text"
                className="input mt-1 w-full text-sm"
                value={newVendorName}
                disabled={addVendorSubmitting}
                onChange={(e) => setNewVendorName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitAddVendor()
                }}
              />
            </div>
            <div className="mt-4">
              <label htmlFor="new-qbo-vendor-id" className="text-xs font-medium text-gray-700">
                QBO vendor ID
              </label>
              <input
                id="new-qbo-vendor-id"
                type="text"
                className="input mt-1 w-full text-sm"
                value={newQboVendorId}
                disabled={addVendorSubmitting}
                onChange={(e) => setNewQboVendorId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitAddVendor()
                }}
              />
            </div>
            {addVendorError ? <p className="mt-3 text-xs text-red-600">{addVendorError}</p> : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary text-sm"
                disabled={addVendorSubmitting}
                onClick={closeAddVendorModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary text-sm"
                disabled={addVendorSubmitting}
                onClick={() => void submitAddVendor()}
              >
                {addVendorSubmitting ? 'Saving…' : 'Add vendor'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
